import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import { useLookups, YEAR_OPTIONS } from '../../lib/lookups';
import {
  FilterBar, MONTH_OPTIONS, defaultFilters, type FilterDef,
} from '../../components/FilterBar';
import { DataTable, type Column } from '../../components/DataTable';
import { api } from '../../lib/api';
import { money, monthYear, useMutation, usePagedList } from '../../lib/hooks';

type TeacherMonthSalary = {
  id: number;
  month: number;
  year: number;
  sharePercent: number;
  paidAmount: number;
  isOpen: boolean;
  teacher?: { id: number; name: string };
};

type StaffMonthSalary = {
  id: number;
  month: number;
  year: number;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  isOpen: boolean;
  staff?: { id: number; name: string };
};

type Expense = {
  id: number;
  description: string;
  amount: number;
  createdDateTime: string;
};

function OpenBadge({ isOpen }: { isOpen: boolean }) {
  return <span className={isOpen ? 'badge badge--partial' : 'badge badge--paid'}>{isOpen ? 'Open' : 'Closed'}</span>;
}

/**
 * Teacher pay.
 *
 * The statement export is the important control here: it is the document teachers are
 * shown, and its layout is unchanged from the old system so month-on-month comparisons
 * still line up.
 */
export function TeacherSalaryPage() {
  const { run, busy } = useMutation();
  const navigate = useNavigate();
  const lookups = useLookups();
  const [generating, setGenerating] = useState(false);

  return (
    <ListPage<TeacherMonthSalary>
      filters={[
        { key: 'teacherId', label: 'Teacher', type: 'select', options: lookups.teachers },
        { key: 'month', label: 'Month', type: 'select', options: MONTH_OPTIONS },
        { key: 'year', label: 'Year', type: 'select', options: YEAR_OPTIONS },
        {
          key: 'isOpen', label: 'Status', type: 'select',
          options: [{ value: 'true', label: 'Open' }, { value: 'false', label: 'Settled' }],
        },
        { key: 'sharePercent', label: 'Share %', type: 'number' },
      ]}
      title="Teacher Salary"
      subtitle="A teacher is paid a percentage of the fees their courses collect."
      endpoint="/api/teacher-salary"
      rowKey={(r) => r.id}
      emptyMessage="No teacher salary records. Use Generate to create this month's rows."
      actions={
        <button
          type="button"
          className="btn"
          disabled={generating}
          onClick={() => {
            setGenerating(true);
            void run(
              () => api.post<{ created: number }>('/api/teacher-salary/generate'),
              "This month's salary records generated.",
            ).finally(() => setGenerating(false));
          }}
        >
          Generate this month
        </button>
      }
      columns={[
        { key: 'teacher', header: 'Teacher', render: (r) => r.teacher?.name ?? `T-${r.id}` },
        { key: 'period', header: 'Period', render: (r) => monthYear(r.month, r.year) },
        { key: 'share', header: 'Share', align: 'right', render: (r) => `${r.sharePercent}%` },
        {
          key: 'paid',
          header: 'Paid',
          align: 'right',
          render: (r) => <span className="money">{money(r.paidAmount)}</span>,
        },
        { key: 'status', header: 'Status', render: (r) => <OpenBadge isOpen={r.isOpen} /> },
      ]}
      rowActions={(row) => (
        <>
        {row.isOpen && (
          <button type="button" className="btn btn--sm" onClick={() => navigate('/salary/teachers/' + row.id + '/pay')}>Pay</button>
        )}
        <button type="button" className="btn btn--sm" onClick={() => navigate('/salary/teachers/' + row.id + '/statement')}>Statement</button>
        <button type="button" className="btn btn--sm" onClick={() => navigate('/salary/teachers/' + row.id + '/payments')}>Payments</button>
        <a
          className="btn btn--sm"
          href={`/api/teacher-salary/${row.id}/statement/export`}
          onClick={(event) => {
            // A plain anchor would drop the bearer token, so fetch it and hand the browser
            // a blob instead.
            event.preventDefault();
            void run(async () => {
              const response = await fetch(`/api/teacher-salary/${row.id}/statement/export`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('ccms.token')}` },
              });
              if (!response.ok) throw new Error('Could not build the statement.');

              const blob = await response.blob();
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `Statement_T-${row.teacher?.id}_${row.month}_${row.year}.xlsx`;
              link.click();
              URL.revokeObjectURL(url);
            }, 'Statement downloaded.');
          }}
        >
          {busy ? 'Working…' : 'Export'}
        </a>
        </>
      )}
    />
  );
}

export function StaffSalaryPage() {
  const { run } = useMutation();
  const navigate = useNavigate();
  const lookups = useLookups();
  const [generating, setGenerating] = useState(false);

  return (
    <ListPage<StaffMonthSalary>
      filters={[
        { key: 'staffId', label: 'Staff', type: 'select', options: lookups.staff },
        { key: 'month', label: 'Month', type: 'select', options: MONTH_OPTIONS },
        { key: 'year', label: 'Year', type: 'select', options: YEAR_OPTIONS },
        {
          key: 'isOpen', label: 'Status', type: 'select',
          options: [{ value: 'true', label: 'Open' }, { value: 'false', label: 'Settled' }],
        },
        { key: 'amountFrom', label: 'Amount from', type: 'number' },
        { key: 'amountTo', label: 'Amount to', type: 'number' },
      ]}
      title="Staff Salary"
      subtitle="Flat monthly amounts, paid down over one or more instalments."
      endpoint="/api/staff-salary"
      rowKey={(r) => r.id}
      emptyMessage="No staff salary records. Use Generate to create this month's rows."
      actions={
        <button
          type="button"
          className="btn"
          disabled={generating}
          onClick={() => {
            setGenerating(true);
            void run(
              () => api.post<{ created: number }>('/api/staff-salary/generate'),
              "This month's salary records generated.",
            ).finally(() => setGenerating(false));
          }}
        >
          Generate this month
        </button>
      }
      columns={[
        { key: 'staff', header: 'Staff', render: (r) => r.staff?.name ?? `STF-${r.id}` },
        { key: 'period', header: 'Period', render: (r) => monthYear(r.month, r.year) },
        {
          key: 'total',
          header: 'Total',
          align: 'right',
          render: (r) => <span className="money">{money(r.totalAmount)}</span>,
        },
        {
          key: 'paid',
          header: 'Paid',
          align: 'right',
          render: (r) => <span className="money">{money(r.paidAmount)}</span>,
        },
        {
          key: 'remaining',
          header: 'Remaining',
          align: 'right',
          render: (r) => <span className="money">{money(r.totalAmount - r.paidAmount)}</span>,
        },
        { key: 'status', header: 'Status', render: (r) => <OpenBadge isOpen={r.isOpen} /> },
      ]}
      rowActions={(row) => (
        row.isOpen
          ? <button type="button" className="btn btn--sm" onClick={() => navigate('/salary/staff/' + row.id + '/pay')}>Pay</button>
          : null
      )}
    />
  );
}

/**
 * Expenses.
 *
 * Create-only by design: the legacy module had no edit or delete, because these rows are
 * financial history. That restriction is preserved.
 */
const EXPENSE_FILTERS: FilterDef[] = [
  { key: 'id', label: 'Expense #', type: 'number' },
  { key: 'description', label: 'Description', type: 'text', wide: true },
  { key: 'amountFrom', label: 'Amount from', type: 'number' },
  { key: 'amountTo', label: 'Amount to', type: 'number' },
  { key: 'dateFrom', label: 'Recorded from', type: 'date' },
  { key: 'dateTo', label: 'Recorded to', type: 'date' },
];

export function ExpensesPage() {
  const list = usePagedList<Expense>('/api/expenses', {}, defaultFilters(EXPENSE_FILTERS));
  const { run, busy } = useMutation();

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');

  async function create() {
    const ok = await run(
      () => api.post('/api/expenses', { description, amount: Number(amount) }),
      'Expense recorded.',
      () => list.reload(),
    );

    if (ok) {
      setDescription('');
      setAmount('');
    }
  }

  const columns: Column<Expense>[] = [
    { key: 'id', header: '#', render: (r) => r.id, width: '70px' },
    { key: 'description', header: 'Description', render: (r) => r.description },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (r) => <span className="money">{money(r.amount)}</span>,
    },
    {
      key: 'date',
      header: 'Recorded',
      render: (r) => (r.createdDateTime ? new Date(r.createdDateTime).toLocaleDateString() : '—'),
    },
  ];

  return (
    <>
      <div className="page__header">
        <div>
          <h2 className="page__title">Expenses</h2>
          <p className="page__subtitle">
            Expenses cannot be edited or deleted once recorded — they are financial history.
          </p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 24 }}>
        <h3 className="card__title">New expense</h3>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="field" style={{ margin: 0, flex: '1 1 260px' }}>
            <label htmlFor="expense-description">Description</label>
            <input
              id="expense-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div className="field" style={{ margin: 0, minWidth: 160 }}>
            <label htmlFor="expense-amount">Amount</label>
            <input
              id="expense-amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
            />
          </div>
          <button
            type="button"
            className="btn btn--accent"
            disabled={busy || !description.trim() || !(Number(amount) > 0)}
            onClick={() => void create()}
          >
            {busy ? 'Saving…' : 'Record expense'}
          </button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={list.data}
        loading={list.loading}
        error={list.error}
        rowKey={(r) => r.id}
        emptyMessage="No expenses recorded."
        onPageChange={list.setPage}
        onPageSizeChange={list.setPageSize}
        above={
          <FilterBar
            filters={EXPENSE_FILTERS}
            values={list.filters}
            onApply={list.setFilters}
            busy={list.loading}
          />
        }
      />
    </>
  );
}
